import {
  FIT_ANIMATION_MS,
  fitTransform,
  resizeTransform,
  type Scene,
  type Transform,
  type ViewGeometry,
  type ViewMode,
  zoomBy,
} from '@better-lights/core';
import { easeOutCubic } from '../../lib/format.ts';
import { createRenderer } from '../../renderer/create.ts';
import type { DepthMap, Pixels, Renderer } from '../../renderer/types.ts';
import { projectState, useDocument } from '../../state/documentStore.ts';
import { useSettings } from '../../state/settingsStore.ts';
import { useUi } from '../../state/uiStore.ts';
import { sceneFor, sceneInputsChanged } from '../scene.ts';
import { CanvasController } from './controller.ts';
import { edgeFieldFor } from './edgeField.ts';
import { createLive, type Live } from './live.ts';
import { drawOverlay, loupeFrame } from './overlay.ts';

export interface EngineOptions {
  forceLdr?: boolean;
  onError?: (message: string) => void;
}

/**
 * Owns the WebGL renderer, the overlay canvas, the pointer controller and the render loop.
 * Render once per edit into the scene texture; each animation frame only presents
 * (pan / zoom / compare / loupe) and redraws the 2D overlay — and only when invalidated.
 */
export class CanvasEngine {
  readonly live: Live = createLive();
  private renderer: Renderer | null = null;
  private readonly controller: CanvasController;
  private photoBitmap: ImageBitmap | null = null;
  private depthMap: DepthMap | null = null;
  private sceneDirty = true;
  private frame = 0;
  private dpr = 1;
  private prevSize = { width: 0, height: 0 };
  private readonly unsub: (() => void)[] = [];
  private readonly resizeObserver: ResizeObserver;
  /** Last scene rendered (for the export and tests). */
  scene: Scene | null = null;

  constructor(
    private readonly stage: HTMLElement,
    private readonly glCanvas: HTMLCanvasElement,
    private readonly overlay: HTMLCanvasElement,
    private readonly opts: EngineOptions = {},
  ) {
    this.createRenderer();
    this.controller = new CanvasController(stage, {
      live: this.live,
      invalidate: () => this.invalidate(),
      geometry: () => this.geometry(),
      photo: () => this.photoBitmap,
      depth: () => this.depthMap,
    });
    this.unsub.push(
      useDocument.subscribe((s, prev) => {
        if (sceneInputsChanged(s, prev)) this.sceneDirty = true;
        if (s.tool !== prev.tool) {
          this.controller.updateCursor();
          if (s.tool === 'freehand' && useSettings.getState().freehandSnap) this.prebuildEdgeField();
          if (s.tool !== 'draw') this.live.hover = null;
        }
        this.invalidate();
      }),
      useSettings.subscribe((s, prev) => {
        if (s.customHex !== prev.customHex) {
          this.sceneDirty = true;
          this.invalidate();
        }
      }),
      useUi.subscribe((s, prev) => {
        if (s.compare !== prev.compare && s.compare) this.live.compareX = this.live.viewW / 2;
        if (s.compare !== prev.compare || s.guidesHidden !== prev.guidesHidden) this.invalidate();
      }),
    );
    this.resizeObserver = new ResizeObserver(() => this.layout());
    this.resizeObserver.observe(stage);
    this.layout();
  }

  private createRenderer(): void {
    try {
      this.renderer = createRenderer(this.glCanvas, {
        forceLdr: this.opts.forceLdr,
        onContextLost: () => cancelAnimationFrame(this.frame),
        onContextRestored: () => {
          this.renderer?.dispose();
          this.createRenderer();
          if (this.photoBitmap) this.renderer?.setPhoto(this.photoBitmap);
          this.renderer?.setDepth(this.depthMap);
          this.sceneDirty = true;
          this.invalidate();
        },
      });
    } catch (e) {
      this.renderer = null;
      this.opts.onError?.(e instanceof Error ? e.message : 'Could not start the renderer.');
    }
  }

  get hdr(): boolean {
    return this.renderer?.hdr ?? false;
  }

  get photo(): ImageBitmap | null {
    return this.photoBitmap;
  }

  setPhoto(bitmap: ImageBitmap): void {
    this.photoBitmap = bitmap;
    try {
      this.renderer?.setPhoto(bitmap);
    } catch (e) {
      this.opts.onError?.(e instanceof Error ? e.message : 'Could not load the photo.');
    }
    this.prevSize = { width: 0, height: 0 };
    this.layout();
    this.sceneDirty = true;
    this.invalidate();
  }

  setDepth(depth: DepthMap | null): void {
    this.depthMap = depth;
    this.renderer?.setDepth(depth);
    this.sceneDirty = true;
    this.invalidate();
  }

  geometry(): ViewGeometry | null {
    const s = projectState();
    if (!s.photo || !this.live.viewW) return null;
    return { viewW: this.live.viewW, viewH: this.live.viewH, imgW: s.photo.width, imgH: s.photo.height };
  }

  /** Fit the photo (⌘0 / Fit button animate over 260 ms, out-cubic). */
  fit(animated: boolean): void {
    const g = this.geometry();
    if (!g) return;
    const to = fitTransform(g);
    if (!animated || matchMedia('(prefers-reduced-motion: reduce)').matches) {
      this.live.fitAnim = null;
      this.live.transform = to;
    } else this.live.fitAnim = { from: { ...this.live.transform }, to, start: performance.now() };
    this.invalidate();
  }

  zoomBy(factor: number): void {
    const g = this.geometry();
    if (!g) return;
    this.live.fitAnim = null;
    this.live.transform = zoomBy(this.live.transform, factor, g);
    this.invalidate();
  }

  setCompareX(x: number): void {
    this.live.compareX = Math.max(0, Math.min(this.live.viewW, x));
    this.invalidate();
  }

  invalidate(): void {
    if (this.frame) return;
    this.frame = requestAnimationFrame(() => {
      this.frame = 0;
      this.draw();
    });
  }

  /** Renders `view` at full photo resolution and reads it back (exports). */
  renderPixels(view: ViewMode): Pixels {
    const r = this.renderer;
    const scene = sceneFor(projectState(), useSettings.getState().customHex);
    if (!r || !scene) throw new Error('The photo is still loading.');
    r.render(scene, view);
    const px = r.readScene();
    this.sceneDirty = true;
    this.invalidate();
    return px;
  }

  dispose(): void {
    cancelAnimationFrame(this.frame);
    this.resizeObserver.disconnect();
    for (const u of this.unsub) u();
    this.controller.dispose();
    this.renderer?.dispose();
    this.renderer = null;
  }

  /* ---------------------------------------------------------------- internals */

  private layout(): void {
    const rect = this.stage.getBoundingClientRect();
    const width = Math.round(rect.width);
    const height = Math.round(rect.height);
    this.dpr = Math.min(2, window.devicePixelRatio || 1);
    this.live.viewW = width;
    this.live.viewH = height;
    this.renderer?.resize(width, height, this.dpr);
    const ow = Math.max(1, Math.round(width * this.dpr));
    const oh = Math.max(1, Math.round(height * this.dpr));
    if (this.overlay.width !== ow) this.overlay.width = ow;
    if (this.overlay.height !== oh) this.overlay.height = oh;
    const s = projectState();
    if (s.photo && width && height) {
      const next = resizeTransform(
        this.live.transform,
        this.prevSize,
        { width, height },
        s.photo.width,
        s.photo.height,
      );
      if (next) this.live.transform = next;
      this.prevSize = { width, height };
    }
    if (useUi.getState().compare) this.live.compareX = Math.min(this.live.compareX, width);
    this.invalidate();
  }

  private prebuildEdgeField(): void {
    setTimeout(() => {
      if (this.photoBitmap && projectState().tool === 'freehand') {
        try {
          edgeFieldFor(this.photoBitmap, this.depthMap);
        } catch {
          // built lazily at stroke end instead
        }
      }
    }, 50);
  }

  private step(now: number): boolean {
    const a = this.live.fitAnim;
    if (!a) return false;
    const u = Math.min(1, (now - a.start) / FIT_ANIMATION_MS);
    const k = easeOutCubic(u);
    const lerp = (x: number, y: number) => x + (y - x) * k;
    this.live.transform = {
      s: lerp(a.from.s, a.to.s),
      tx: lerp(a.from.tx, a.to.tx),
      ty: lerp(a.from.ty, a.to.ty),
    } as Transform;
    if (u >= 1) this.live.fitAnim = null;
    return this.live.fitAnim !== null;
  }

  private draw(): void {
    const animating = this.step(performance.now());
    const r = this.renderer;
    const s = projectState();
    const ui = useUi.getState();
    if (r && this.photoBitmap && this.sceneDirty) {
      this.sceneDirty = false;
      const scene = sceneFor(s, useSettings.getState().customHex);
      if (scene) {
        r.render(scene, s.view);
        this.scene = scene;
      }
    }
    if (r) {
      const loupe = loupeFrame(this.live);
      r.present({
        transform: this.live.transform,
        viewW: this.live.viewW,
        viewH: this.live.viewH,
        dpr: this.dpr,
        compareX: ui.compare ? this.live.compareX : null,
        loupe,
      });
    }
    const ctx = this.overlay.getContext('2d');
    if (ctx) {
      ctx.setTransform(1, 0, 0, 1, 0, 0);
      ctx.clearRect(0, 0, this.overlay.width, this.overlay.height);
      ctx.setTransform(this.dpr, 0, 0, this.dpr, 0, 0);
      if (s.photo) {
        drawOverlay(
          ctx,
          {
            runs: s.runs,
            photoW: s.photo.width,
            photoH: s.photo.height,
            focusId: s.activeRunId ?? s.selectedRunId,
            drawingId: s.activeRunId,
            tool: s.tool,
            guides: !ui.guidesHidden,
          },
          this.live,
          ui.compare,
        );
      }
    }
    if (animating) this.invalidate();
  }
}

let current: CanvasEngine | null = null;
/** The mounted editor engine (keyboard commands, export). */
export const engineRef = {
  get: () => current,
  set: (e: CanvasEngine | null) => {
    current = e;
  },
};
